import { useCallback, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'

interface PullToRefreshProps {
  onRefresh: () => Promise<void>
  children: React.ReactNode
  className?: string
}

const THRESHOLD = 80
const MAX_PULL = 120

export function PullToRefresh({
  onRefresh,
  children,
  className,
}: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const startY = useRef(0)
  const pulling = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const getScrollParent = useCallback(
    (el: HTMLElement | null): HTMLElement | null => {
      if (!el) return null
      let parent = el.parentElement
      while (parent) {
        const { overflowY } = getComputedStyle(parent)
        if (overflowY === 'auto' || overflowY === 'scroll') return parent
        parent = parent.parentElement
      }
      return null
    },
    [],
  )

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isRefreshing) return
      const scrollParent = getScrollParent(containerRef.current)
      const scrollTop = scrollParent ? scrollParent.scrollTop : 0
      if (scrollTop <= 0) {
        startY.current = e.touches[0].clientY
        pulling.current = true
      }
    },
    [isRefreshing, getScrollParent],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!pulling.current || isRefreshing) return
      const currentY = e.touches[0].clientY
      const diff = currentY - startY.current
      if (diff > 0) {
        // Apply resistance - the further you pull, the harder it gets
        const distance = Math.min(diff * 0.5, MAX_PULL)
        setPullDistance(distance)
      } else {
        pulling.current = false
        setPullDistance(0)
      }
    },
    [isRefreshing],
  )

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current || isRefreshing) return
    pulling.current = false

    if (pullDistance >= THRESHOLD) {
      setIsRefreshing(true)
      setPullDistance(THRESHOLD * 0.5) // Show indicator while refreshing
      try {
        await onRefresh()
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }
  }, [pullDistance, isRefreshing, onRefresh])

  const progress = Math.min(pullDistance / THRESHOLD, 1)

  return (
    <div
      ref={containerRef}
      className={className}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-[height] duration-200 ease-out"
        style={{
          height:
            pullDistance > 0 || isRefreshing ? `${pullDistance}px` : '0px',
          transition: pulling.current ? 'none' : undefined,
        }}
      >
        <Loader2
          className={`h-5 w-5 text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`}
          style={{
            opacity: progress,
            transform: `rotate(${progress * 360}deg)`,
          }}
        />
      </div>
      {children}
    </div>
  )
}
